import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Building2, DollarSign, Eye, Globe, LayoutGrid, List, Loader2, MapPin, Phone, Users } from 'lucide-react';
import { api } from '../App';
import { usePageTitle } from '../hooks/usePageTitle';
import Chip from '../components/Chip';
import RecordCount from '../components/RecordCount';
import SearchField from '../components/SearchField';
import ViewToggle from '../components/ViewToggle';
import FilterActions from '../components/FilterActions';
import { 
  StandardTable,
  StandardTableHeader, 
  StandardTableHeaderCell,
  StandardTableBody,
  StandardTableRow,
  StandardTableCell 
} from '../components/StandardTable';

interface NonProfitOrganization {
  id: string;
  charitable_organization_name: string;
  ein?: string;
  registration_number?: string;
  dbas?: string;
  principal_address_1?: string;
  principal_city?: string;
  principal_state?: string;
  principal_postal_code?: string;
  phone?: string;
  email?: string;
  website?: string;
  legal_form?: string;
  current_status?: string;
  simplified_status?: string;
  status_date?: string;
  expiration_date?: string;
  ntee_code_1?: string;
  ntee_code_2?: string;
  ntee_code_3?: string;
  total_revenue?: number;
  total_expenses?: number;
  total_assets_amount?: number;
  organization_size?: string;
  program_service_ratio?: number;
  fundraising_ratio?: number;
  created_at: string;
  updated_at: string;
}

interface NonProfitsResponse {
  organizations: NonProfitOrganization[];
  pagination: {
    page: number;
    limit: number;
    total: number;
    totalPages: number;
  };
}

interface FilterOptions {
  cities: string[];
  states: string[];
  statuses: string[];
  legal_forms: string[];
  ntee_codes: string[];
}

const NonProfitsPage: React.FC = () => {
  const navigate = useNavigate();
  const [viewMode, setViewMode] = useState<'table' | 'card'>('table');
  
  // Set page title in header
  usePageTitle('Non-Profit Organizations', 'Browse and search registered non-profit organizations in Colorado');
  const [searchQuery, setSearchQuery] = useState('');
  // Default to CO since that's our primary data source
  // Note: State filter is hardcoded to 'CO' as the application currently only supports Colorado data.
  // Future enhancement: Add state configuration when expanding to other states.
  const [filters, setFilters] = useState({
    city: '',
    state: 'CO',
    status: '',
    legal_form: '',
    ntee_code: '',
    revenue_min: '',
    revenue_max: ''
  });
  const [sortBy, setSortBy] = useState<string>('organization');
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('asc');
  const [nonprofits, setNonprofits] = useState<NonProfitOrganization[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const [totalCount, setTotalCount] = useState(0);
  const [filterOptions, setFilterOptions] = useState<FilterOptions>({
    cities: [],
    states: [],
    statuses: [],
    legal_forms: [],
    ntee_codes: []
  });

  // Refs for infinite scroll to prevent stale closures
  const stateRef = useRef({ loadingMore, hasMore, loading, nonprofitsCount: nonprofits.length });
  const fetchNonProfitsRef = useRef<(pageNum: number, append: boolean) => Promise<void>>(null as any);

  // Fetch non-profits with current filters and search
  const fetchNonProfits = useCallback(async (pageNum = 1, append = false) => {
    try {
      if (append) {
        setLoadingMore(true);
      } else {
        setLoading(true);
      }

      const params = new URLSearchParams({
        page: pageNum.toString(),
        limit: '50', // Load 50 items at a time for infinite scroll
        ...(searchQuery && { search: searchQuery }),
        ...(filters.city && { city: filters.city }),
        ...(filters.state && { state: filters.state }),
        ...(filters.status && { status: filters.status }),
        ...(filters.legal_form && { legal_form: filters.legal_form }),
        ...(filters.ntee_code && { ntee_code: filters.ntee_code }),
        ...(filters.revenue_min && { revenue_min: filters.revenue_min }),
        ...(filters.revenue_max && { revenue_max: filters.revenue_max }),
        sortBy: sortBy,
        sortDirection: sortDirection
      });

      const url = `/non-profits?${params}`;
      const response = await api.get(url);
      
      if (response.data.success) {
        const data: NonProfitsResponse = response.data.data;
        
        if (append) {
          setNonprofits(prev => [...prev, ...data.organizations]);
        } else {
          setNonprofits(data.organizations);
        }
        
        setTotalCount(data.pagination.total);
        setHasMore(pageNum < data.pagination.totalPages);
      }
    } catch (error) {
      console.error('Error fetching non-profits:', error);
      if (!append) {
        setNonprofits([]);
      }
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  }, [searchQuery, filters, sortBy, sortDirection]);

  // Fetch filter options
  const fetchFilterOptions = async () => {
    try {
      const response = await api.get('/non-profits/filters/options');
      if (response.data.success) {
        setFilterOptions(response.data.data);
      }
    } catch (error) {
      console.error('Error fetching filter options:', error);
    }
  };

  // Update refs when state changes
  useEffect(() => {
    stateRef.current = { loadingMore, hasMore, loading, nonprofitsCount: nonprofits.length };
    fetchNonProfitsRef.current = fetchNonProfits;
  }, [loadingMore, hasMore, loading, nonprofits.length, fetchNonProfits]);

  // Initial data fetch
  useEffect(() => {
    fetchFilterOptions();
    fetchNonProfits(1, false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Refetch when filters, search, or sorting change
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      fetchNonProfits(1, false); // Reset to page 1 when filters change
    }, 300); // Debounce search

    return () => clearTimeout(timeoutId);
  }, [searchQuery, filters, sortBy, sortDirection, fetchNonProfits]);

  // Stable scroll handler for infinite scroll
  const handleScrollStable = useCallback(() => {
    const { loadingMore: currentLoadingMore, hasMore: currentHasMore, loading: currentLoading } = stateRef.current;
    
    if (currentLoadingMore || !currentHasMore || currentLoading) {
      return;
    }

    // Find the visible main container
    const findVisibleMainContainer = (): HTMLElement | null => {
      const mainContainers = document.querySelectorAll<HTMLElement>('main.overflow-y-auto');
      
      for (const container of mainContainers) {
        const rect = container.getBoundingClientRect();
        const computedStyle = window.getComputedStyle(container);
        const isVisible = rect.width > 0 && rect.height > 0;
        const isDisplayed = computedStyle.display !== 'none';
        
        if (isVisible && isDisplayed) {
          return container;
        }
      }
      return null;
    };

    const container = findVisibleMainContainer();
    if (!container) return;

    const scrollTop = container.scrollTop;
    const scrollHeight = container.scrollHeight;
    const clientHeight = container.clientHeight;

    // Trigger load when 300px from bottom
    if (scrollTop + clientHeight >= scrollHeight - 300) {
      const nextPage = Math.floor(stateRef.current.nonprofitsCount / 50) + 1;
      fetchNonProfitsRef.current(nextPage, true);
    }
  }, []);

  // Attach scroll listener
  useEffect(() => {
    const mainContainers = document.querySelectorAll<HTMLElement>('main.overflow-y-auto');
    
    // Find the visible main container
    const activeContainer = Array.from(mainContainers).find((container) => {
      const rect = container.getBoundingClientRect();
      const isVisible = rect.width > 0 && rect.height > 0;
      const computedStyle = window.getComputedStyle(container);
      const isDisplayed = computedStyle.display !== 'none';
      return isVisible && isDisplayed;
    });
    
    if (activeContainer) {
      activeContainer.addEventListener('scroll', handleScrollStable, { passive: true });
      
      return () => {
        activeContainer.removeEventListener('scroll', handleScrollStable);
      };
    }
    
    return undefined;
  }, [handleScrollStable]);

  const handleClearFilters = () => {
    setFilters({
      city: '',
      state: 'CO', // Keep Colorado as default when clearing filters
      status: '',
      legal_form: '',
      ntee_code: '',
      revenue_min: '',
      revenue_max: ''
    });
    setSearchQuery('');
  };

  // Handle sorting
  const handleSort = (field: string) => {
    if (sortBy === field) {
      // Toggle direction if clicking the same column
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      // Set new sort field with ascending as default
      setSortBy(field);
      setSortDirection('asc');
    }
  };


  const getStatusVariant = (status: string) => {
    switch (status?.toLowerCase()) {
      case 'active':
      case 'current':
        return 'success';
      case 'inactive':
      case 'expired':
        return 'neutral';
      case 'suspended':
      case 'revoked':
        return 'danger';
      default:
        return 'neutral';
    }
  };

  const getLegalFormColor = (form: string) => {
    const colors = [
      'bg-blue-100 text-blue-800',
      'bg-green-100 text-green-800',
      'bg-purple-100 text-purple-800',
      'bg-orange-100 text-orange-800',
      'bg-pink-100 text-pink-800'
    ];
    return colors[Math.abs(form.split('').reduce((a, b) => a + b.charCodeAt(0), 0)) % colors.length];
  };

  const formatCurrency = (amount: number) => {
    if (amount >= 1000000) {
      return `$${(amount / 1000000).toFixed(1)}M`;
    } else if (amount >= 1000) {
      return `$${(amount / 1000).toFixed(0)}K`;
    }
    return `$${amount?.toLocaleString() || 0}`;
  };

  const renderCard = (nonprofit: NonProfitOrganization) => (
    <div
      key={nonprofit.id}
      className="card hover:shadow-lg transition-shadow duration-200 cursor-pointer"
      onClick={() => navigate(`/non-profits/${nonprofit.id}`)}
    >
      <div className="flex justify-between items-start mb-4">
        <div className="flex-1">
          <h3 className="text-lg font-semibold text-text-primary dark:text-text-dark-primary mb-1">
            {nonprofit.charitable_organization_name}
          </h3>
          {nonprofit.ein && (
            <div className="flex items-center text-sm text-text-secondary dark:text-text-dark-secondary mb-2">
              <Building2 className="w-4 h-4 mr-1" />
              EIN: {nonprofit.ein}
            </div>
          )}
        </div>
        <div className="flex flex-col gap-1">
          <Chip 
            label={nonprofit.simplified_status || nonprofit.current_status || 'Unknown'}
            variant={getStatusVariant(nonprofit.simplified_status || nonprofit.current_status || 'Unknown')}
            size="sm"
          />
        </div>
      </div>

      <div className="space-y-2 mb-4">
        {nonprofit.principal_city && nonprofit.principal_state && (
          <div className="flex items-center text-sm text-text-secondary dark:text-text-dark-secondary">
            <MapPin className="w-4 h-4 mr-2" />
            {nonprofit.principal_city}, {nonprofit.principal_state}
          </div>
        )}
        {nonprofit.phone && (
          <div className="flex items-center text-sm text-text-secondary dark:text-text-dark-secondary">
            <Phone className="w-4 h-4 mr-2" />
            {nonprofit.phone}
          </div>
        )}
        {nonprofit.website && (
          <div className="flex items-center text-sm text-text-secondary dark:text-text-dark-secondary">
            <Globe className="w-4 h-4 mr-2" />
            <a 
              href={nonprofit.website} 
              target="_blank" 
              rel="noopener noreferrer"
              className="hover:text-primary-600 dark:hover:text-primary-400"
              onClick={(e) => e.stopPropagation()}
            >
              {nonprofit.website}
            </a>
          </div>
        )}
        {nonprofit.total_revenue && (
          <div className="flex items-center text-sm text-text-secondary dark:text-text-dark-secondary">
            <DollarSign className="w-4 h-4 mr-2" />
            Revenue: {formatCurrency(nonprofit.total_revenue)}
          </div>
        )}
      </div>

      <div className="flex flex-wrap gap-2">
        {nonprofit.legal_form && (
          <span className={`px-2 py-1 text-xs font-medium rounded-full ${getLegalFormColor(nonprofit.legal_form)}`}>
            {nonprofit.legal_form}
          </span>
        )}
        {nonprofit.ntee_code_1 && (
          <span className="px-2 py-1 text-xs font-medium rounded-full bg-background-tertiary text-text-primary dark:bg-background-dark-tertiary dark:text-text-dark-primary">
            {nonprofit.ntee_code_1}
          </span>
        )}
        {nonprofit.organization_size && (
          <span className="px-2 py-1 text-xs font-medium rounded-full bg-purple-100 text-purple-800">
            <Users className="w-3 h-3 inline mr-1" />
            {nonprofit.organization_size}
          </span>
        )}
      </div>

      <div className="mt-4 pt-4 border-t border-border-light dark:border-border-dark">
        <button className="w-full flex items-center justify-center gap-2 text-sm text-primary-600 hover:text-primary-700 font-medium">
          <Eye className="w-4 h-4" />
          View Details
        </button>
      </div>
    </div>
  );

  const renderTable = () => (
    <StandardTable loading={loading}>
      <StandardTableHeader>
        <tr>
          <StandardTableHeaderCell 
            sortable
            sortKey="organization"
            currentSortKey={sortBy}
            sortDirection={sortDirection}
            onSort={handleSort}
          >
            Organization
          </StandardTableHeaderCell>
          <StandardTableHeaderCell 
            responsiveHide="md"
            columnType="fit"
          >
            EIN
          </StandardTableHeaderCell>
          <StandardTableHeaderCell responsiveHide="md">
            Location
          </StandardTableHeaderCell>
          <StandardTableHeaderCell 
            responsiveHide="lg"
            columnType="fit"
          >
            Legal Form
          </StandardTableHeaderCell>
          <StandardTableHeaderCell columnType="fit">
            Status
          </StandardTableHeaderCell>
          <StandardTableHeaderCell 
            sortable
            sortKey="revenue"
            currentSortKey={sortBy}
            sortDirection={sortDirection}
            onSort={handleSort}
            responsiveHide="md"
            columnType="fit"
          >
            Revenue
          </StandardTableHeaderCell>
          <StandardTableHeaderCell columnType="actions">
            Actions
          </StandardTableHeaderCell>
        </tr>
      </StandardTableHeader>
      <StandardTableBody>
        {nonprofits.map((nonprofit) => (
          <StandardTableRow 
            key={nonprofit.id} 
            onClick={() => navigate(`/non-profits/${nonprofit.id}`)}
            hoverable
          >
            <StandardTableCell>
              <div className="font-medium text-text-primary dark:text-text-dark-primary">
                {nonprofit.charitable_organization_name}
              </div>
              {nonprofit.phone && (
                <div className="text-text-secondary dark:text-text-dark-secondary">
                  {nonprofit.phone}
                </div>
              )}
            </StandardTableCell>
            <StandardTableCell responsiveHide="md">
              {nonprofit.ein || '-'}
            </StandardTableCell>
            <StandardTableCell responsiveHide="md">
              {nonprofit.principal_city && nonprofit.principal_state
                ? `${nonprofit.principal_city}, ${nonprofit.principal_state}`
                : '-'}
            </StandardTableCell>
            <StandardTableCell responsiveHide="lg">
              {nonprofit.legal_form ? (
                <span className={`px-2 py-1 text-xs font-medium rounded-full ${getLegalFormColor(nonprofit.legal_form)}`}>
                  {nonprofit.legal_form}
                </span>
              ) : '-'}
            </StandardTableCell>
            <StandardTableCell>
              <Chip 
                label={nonprofit.simplified_status || nonprofit.current_status || 'Unknown'}
                variant={getStatusVariant(nonprofit.simplified_status || nonprofit.current_status || 'Unknown')}
                size="sm"
              />
            </StandardTableCell>
            <StandardTableCell responsiveHide="md">
              {nonprofit.total_revenue ? formatCurrency(nonprofit.total_revenue) : '-'}
            </StandardTableCell>
            <StandardTableCell align="center">
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  navigate(`/non-profits/${nonprofit.id}`);
                }}
                className="p-1 text-text-tertiary hover:text-text-secondary dark:text-text-dark-tertiary dark:hover:text-text-dark-secondary transition-colors"
                title="View details"
                aria-label="View details"
              >
                <Eye className="w-4 h-4" />
              </button>
            </StandardTableCell>
          </StandardTableRow>
        ))}
      </StandardTableBody>
    </StandardTable>
  );

  return (
    <div className="space-y-6 pt-2">

      {/* Search and Filters */}
      <div className="card">
        <div className="space-y-4">
          {/* Search Bar */}
          <SearchField
            value={searchQuery}
            onChange={setSearchQuery}
            placeholder="Search organizations by name, EIN, or address..."
            className="w-full"
          />

          {/* Filters */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-4">
            <div>
              <label className="block text-sm font-medium text-text-primary dark:text-text-dark-primary mb-1">
                City
              </label>
              <select
                value={filters.city}
                onChange={(e) => setFilters(prev => ({ ...prev, city: e.target.value }))}
                className="input"
              >
                <option value="">All cities</option>
                {filterOptions.cities.map(city => (
                  <option key={city} value={city}>{city}</option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-text-primary dark:text-text-dark-primary mb-1">
                State
              </label>
              <select
                value={filters.state}
                onChange={(e) => setFilters(prev => ({ ...prev, state: e.target.value }))}
                className="input"
              >
                <option value="">All states</option>
                {filterOptions.states.map(state => (
                  <option key={state} value={state}>
                    {state}{state === 'CO' ? ' (default)' : ''}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-text-primary dark:text-text-dark-primary mb-1">
                Status
              </label>
              <select
                value={filters.status}
                onChange={(e) => setFilters(prev => ({ ...prev, status: e.target.value }))}
                className="input"
              >
                <option value="">All statuses</option>
                {filterOptions.statuses.map(status => (
                  <option key={status} value={status}>{status}</option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-text-primary dark:text-text-dark-primary mb-1">
                Legal Form
              </label>
              <select
                value={filters.legal_form}
                onChange={(e) => setFilters(prev => ({ ...prev, legal_form: e.target.value }))}
                className="input"
              >
                <option value="">All forms</option>
                {filterOptions.legal_forms.map(form => (
                  <option key={form} value={form}>{form}</option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-text-primary dark:text-text-dark-primary mb-1">
                NTEE Code
              </label>
              <select
                value={filters.ntee_code}
                onChange={(e) => setFilters(prev => ({ ...prev, ntee_code: e.target.value }))}
                className="input"
              >
                <option value="">All codes</option>
                {filterOptions.ntee_codes.map(code => (
                  <option key={code} value={code}>{code}</option>
                ))}
              </select>
            </div>
          </div>

          {/* Revenue Range Filter */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-text-primary dark:text-text-dark-primary mb-1">
                Minimum Revenue
              </label>
              <div className="relative">
                <DollarSign className="absolute left-3 top-1/2 transform -translate-y-1/2 text-text-tertiary dark:text-text-dark-tertiary w-4 h-4" />
                <input
                  type="number"
                  placeholder="0"
                  value={filters.revenue_min}
                  onChange={(e) => setFilters(prev => ({ ...prev, revenue_min: e.target.value }))}
                  className="input pl-9"
                  min="0"
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-text-primary dark:text-text-dark-primary mb-1">
                Maximum Revenue
              </label>
              <div className="relative">
                <DollarSign className="absolute left-3 top-1/2 transform -translate-y-1/2 text-text-tertiary dark:text-text-dark-tertiary w-4 h-4" />
                <input
                  type="number"
                  placeholder="No limit"
                  value={filters.revenue_max}
                  onChange={(e) => setFilters(prev => ({ ...prev, revenue_max: e.target.value }))}
                  className="input pl-9"
                  min="0"
                />
              </div>
            </div>
          </div>

          <FilterActions
            onClear={handleClearFilters}
            hasFilters={Boolean(searchQuery) || Object.values(filters).some(f => f)}
          />
        </div>
      </div>

      {/* View Toggle and Results Count */}
      <div className="flex justify-between items-center">
        {/* View toggle - hidden on mobile (cards only on mobile) */}
        <div className="hidden lg:flex items-center gap-2">
          <ViewToggle
            value={viewMode}
            onChange={setViewMode}
            options={[
              { id: 'card', label: 'Card view', icon: LayoutGrid },
              { id: 'table', label: 'List view', icon: List },
            ]}
          />
        </div>
        <RecordCount
          label="organizations"
          showing={nonprofits.length}
          filtered={totalCount}
          total={totalCount}
          isLoading={loading}
        />
      </div>

      {/* Results */}
      {loading ? (
        <div className="flex justify-center items-center py-12">
          <Loader2 className="w-8 h-8 animate-spin text-primary-600" />
        </div>
      ) : (
        <>
          {/* Content */}
          {viewMode === 'card' ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {nonprofits.map(renderCard)}
            </div>
          ) : (
            renderTable()
          )}

          {/* Loading more indicator */}
          {loadingMore && (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="w-6 h-6 animate-spin text-primary-600" />
              <span className="ml-2 text-text-secondary dark:text-text-dark-secondary">Loading more...</span>
            </div>
          )}

          {/* End of results indicator */}
          {!loading && !loadingMore && !hasMore && nonprofits.length > 0 && (
            <div className="text-center py-8 text-text-tertiary dark:text-text-dark-tertiary">
              <p>You&apos;ve reached the end of the list</p>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default NonProfitsPage;
